import { createRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@arabquiz/ui'
import { ArrowLeft } from 'lucide-react'
import { Route as appRoute } from '../../../_app'
import { GameStateProvider, useGameState } from '../../../../game/GameStateProvider'
import { PlayerGameView } from '../../../../game/PlayerGameView'
import {
  readStoredPlayerSession,
  type StoredPlayerSession,
} from '../../../../game/playerTokenStorage'

/**
 * `/games/play/$gameId` — URL-addressable player surface for an
 * already-joined session.
 *
 * Sits under the authenticated `_app` layout because the only meaningful
 * way to land here is either:
 *   - A host wanting to spectate / co-play their own quiz (already
 *     signed in, picked up from the dashboard); or
 *   - A player whose anonymous `/play` session was suspended and who
 *     reconnected on the same device using a stored `playerToken`.
 *
 * The route mounts its own `GameStateProvider` (so the SignalR connection
 * is bound to the route lifecycle, just like the host panel) and
 * dispatches `Reconnect(gameId, playerToken)` exactly once on mount. We
 * read the token from `localStorage` under a stable key so a `/play`
 * session that successfully joined can survive a hard refresh; if no
 * token is present, the route falls back to a "no token" guard with a
 * link back to the public join page.
 *
 * The visible surface is `<PlayerGameView />` — the same shared component
 * used by `/play` post-join — keeping a single source of truth for every
 * phase view a player can see.
 */
export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/games/play/$gameId',
  component: PlayGameRoute,
})

function PlayGameRoute() {
  return (
    <GameStateProvider>
      <PlayGameInner />
    </GameStateProvider>
  )
}

function PlayGameInner() {
  const { t } = useTranslation('game')
  const { gameId } = Route.useParams()
  const { dispatch, invoke } = useGameState()

  // Read the persisted token once on mount. We capture it into local
  // state so the guard branch can decide what to render *before* the
  // reconnect round-trip resolves — without this, the route would
  // briefly flash a "connecting" panel even for tokenless visitors.
  const [storedSession] = useState<StoredPlayerSession | null>(() => readStoredPlayerSession())

  // Single reconnect dispatch per (gameId, token) pair. The hub's
  // `invoke()` already awaits the connection handshake so we don't need
  // a separate readiness gate.
  useEffect(() => {
    if (!storedSession || storedSession.gameId !== gameId) return
    dispatch({
      type: 'RESTORE_PLAYER_SESSION',
      gameId: storedSession.gameId,
      token: storedSession.token,
      nickname: storedSession.nickname,
    })
    void invoke('Reconnect', gameId, storedSession.token)
  }, [dispatch, gameId, invoke, storedSession])

  if (!storedSession || storedSession.gameId !== gameId) {
    return <NoTokenGuard message={t('noPlayerToken')} backLabel={t('backToJoin')} />
  }

  return <PlayerGameView />
}

type NoTokenGuardProps = {
  message: string
  backLabel: string
}

/**
 * Terminal "you are not actually in a game" screen. Shown when the route
 * is hit directly (no stored token) — there is no useful Reconnect call
 * we could make, so the friendliest outcome is to point the visitor
 * back at the canonical join page.
 */
function NoTokenGuard({ message, backLabel }: NoTokenGuardProps) {
  return (
    <main className="relative min-h-screen overflow-hidden bg-gradient-to-bl from-brand-900 via-brand-800 to-brand-900 text-white">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.15),transparent_60%)]"
        aria-hidden="true"
      />
      <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center ps-4 pe-4 py-10 text-center">
        <p className="text-base leading-relaxed text-brand-100/90" role="alert">
          {message}
        </p>
        <a href="/play" className="mt-6 inline-block">
          <Button
            type="button"
            size="lg"
            className="h-12 gap-2 bg-white text-brand-900 font-bold shadow-xl hover:bg-brand-50 ps-6 pe-6"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            <span>{backLabel}</span>
          </Button>
        </a>
      </div>
    </main>
  )
}
